import { useFormikContext } from 'formik';
import styled from 'styled-components';
import { FormType, InterestInstructions } from '../types';
import { CheckBox, Select, TextArea, TextFiled } from 'tsx/comps/form';
import { get } from 'lodash';

const Div = styled.div``;

interface Step4Props {
  onNext: () => void;
  onPrev: () => void;
}

export const Step4: React.FC<Step4Props> = ({ onNext, onPrev }) => {
  const { values, handleChange, handleBlur, touched, errors, setFieldValue, setFieldTouched, validateField } =
    useFormikContext<FormType>();

  const validate = () => {
    const timer = setTimeout(() => {
      validateField('interestRequirement');
      clearTimeout(timer);
    }, 1);
  };

  return (
    <Div>
      <h2>Declaration</h2>
      <div className="spc30" />
      <h5>Interest Instructions</h5>
      <div className="spc30" />
      <p>
        Please refer to product terms and conditions for interest payment options available to the account you are
        applying for. Interest transferred monthly is paid on the 1st working day of the following month. For weekends
        and public holidays the interest will be received on the 1st working day following the 1st of the month. For
        interest transferred annually please refer to the details in the product guide provided.
      </p>
      <div className="spc30" />
      <div className="input-grid">
        <div>
          <span className="label">I/We require interest to be</span>
          <Select
            id="interestRequirement.interestInstructions.value"
            value={values.interestRequirement.interestInstructions.value}
            onChange={(e) => {
              handleChange(e);
              if (e.target.value === InterestInstructions.AddedToAccount) {
                setFieldValue('interestRequirement.interestInstructions.chorleyAccountNumber', '');
                setFieldValue('interestRequirement.interestInstructions.bank', null);
                setFieldTouched('interestRequirement.interestInstructions.bank', undefined);
                setFieldTouched('interestRequirement.interestInstructions.chorleyAccountNumber', undefined);
              }
              if (e.target.value === InterestInstructions.PaidToAnotherAccount) {
                setFieldValue('interestRequirement.interestInstructions.bank', null);
                setFieldTouched('interestRequirement.interestInstructions.bank', undefined);
                setFieldTouched('interestRequirement.interestInstructions.chorleyAccountNumber', undefined);
              }
              if (e.target.value === InterestInstructions.PaidToBankAccount) {
                setFieldValue('interestRequirement.interestInstructions.chorleyAccountNumber', '');
                setFieldTouched('interestRequirement.interestInstructions.chorleyAccountNumber', undefined);
                setFieldValue('interestRequirement.interestInstructions.bank', {
                  nameOfAccountHolder: '',
                  name: '',
                  currentAddress: '',
                  postcode: '',
                  sortcode: '',
                  accountNumber: '',
                });
              }

              validate();
            }}
            onBlur={handleBlur}
            error={Boolean(
              get(touched, 'interestRequirement.interestInstructions.value') &&
                get(errors, 'interestRequirement.interestInstructions.value'),
            )}
            $hasValue={!!get(values, 'interestRequirement.interestInstructions.value')}
          >
            <option value="" disabled selected>
              Select
            </option>
            <option value={InterestInstructions.AddedToAccount}>Added to the account</option>
            <option value={InterestInstructions.PaidToAnotherAccount}>
              Paid direct to another Chorley Building Society Account
            </option>
            <option value={InterestInstructions.PaidToBankAccount}>Paid direct to a bank account</option>
          </Select>
        </div>
      </div>
      {values.interestRequirement.interestInstructions.value === InterestInstructions.PaidToAnotherAccount && (
        <>
          <div className="spc30" />
          <div className="input-grid">
            <div>
              <span className="label">Chorley Building Society Account Number</span>
              <TextFiled
                id="interestRequirement.interestInstructions.chorleyAccountNumber"
                $fullWidth
                value={values.interestRequirement.interestInstructions.chorleyAccountNumber}
                onBlur={handleBlur}
                onChange={handleChange}
                error={Boolean(
                  get(touched, 'interestRequirement.interestInstructions.chorleyAccountNumber') &&
                    get(errors, 'interestRequirement.interestInstructions.chorleyAccountNumber'),
                )}
              />
            </div>
          </div>
        </>
      )}

      {values.interestRequirement.interestInstructions.value === InterestInstructions.PaidToBankAccount && (
        <>
          <div className="spc30" />
          <p>Please provide details of the bank account you would like interest paid into</p>
          <div className="spc30" />
          <div className="input-grid">
            <div>
              <span className="label">Name of Account Holder</span>
              <TextFiled
                id="interestRequirement.interestInstructions.bank.nameOfAccountHolder"
                $fullWidth
                value={values.interestRequirement.interestInstructions.bank?.nameOfAccountHolder}
                onBlur={handleBlur}
                onChange={handleChange}
                error={Boolean(
                  get(touched, 'interestRequirement.interestInstructions.bank.nameOfAccountHolder') &&
                    get(errors, 'interestRequirement.interestInstructions.bank.nameOfAccountHolder'),
                )}
              />
            </div>
            <div>
              <span className="label">Bank Name</span>
              <TextFiled
                id="interestRequirement.interestInstructions.bank.name"
                $fullWidth
                value={values.interestRequirement.interestInstructions.bank?.name}
                onBlur={handleBlur}
                onChange={handleChange}
                error={Boolean(
                  get(touched, 'interestRequirement.interestInstructions.bank.name') &&
                    get(errors, 'interestRequirement.interestInstructions.bank.name'),
                )}
              />
            </div>
          </div>
          <div className="spc30" />
          <span className="label">Bank Address</span>
          <div className="spc10" />
          <TextArea
            id="interestRequirement.interestInstructions.bank.currentAddress"
            $fullWidth
            value={values.interestRequirement.interestInstructions.bank?.currentAddress}
            rows={5}
            onBlur={handleBlur}
            onChange={handleChange}
            error={Boolean(
              get(touched, 'interestRequirement.interestInstructions.bank.currentAddress') &&
                get(errors, 'interestRequirement.interestInstructions.bank.currentAddress'),
            )}
          />
          <div className="spc30" />
          <div className="input-grid">
            <div>
              <span className="label">Bank Postcode</span>
              <TextFiled
                id="interestRequirement.interestInstructions.bank.postcode"
                $fullWidth
                value={values.interestRequirement.interestInstructions.bank?.postcode}
                onBlur={handleBlur}
                onChange={handleChange}
                error={Boolean(
                  get(touched, 'interestRequirement.interestInstructions.bank.postcode') &&
                    get(errors, 'interestRequirement.interestInstructions.bank.postcode'),
                )}
              />
            </div>
            <div>
              <span className="label">Sort Code</span>
              <TextFiled
                id="interestRequirement.interestInstructions.bank.sortcode"
                $fullWidth
                value={values.interestRequirement.interestInstructions.bank?.sortcode}
                onBlur={handleBlur}
                onChange={handleChange}
                error={Boolean(
                  get(touched, 'interestRequirement.interestInstructions.bank.sortcode') &&
                    get(errors, 'interestRequirement.interestInstructions.bank.sortcode'),
                )}
              />
            </div>
          </div>
          <div className="spc30" />
          <div className="input-grid">
            <div>
              <span className="label">Account Number</span>
              <TextFiled
                id="interestRequirement.interestInstructions.bank.accountNumber"
                $fullWidth
                value={values.interestRequirement.interestInstructions.bank?.accountNumber}
                type="number"
                onKeyPress={(event) => {
                  if (!/[0-9]/.test(event.key)) {
                    event.preventDefault();
                  }
                }}
                onBlur={handleBlur}
                onChange={handleChange}
                error={Boolean(
                  get(touched, 'interestRequirement.interestInstructions.bank.accountNumber') &&
                    get(errors, 'interestRequirement.interestInstructions.bank.accountNumber'),
                )}
              />
            </div>
          </div>
        </>
      )}
      <div className="spc30" />
      <div className="spc30" />
      <h5>Declaration</h5>
      <div className="spc30" />
      <p>I/We the person(s) whose signature(s) appear on this form declare that:</p>
      <div className="spc30" />
      <div className="checkbox-field">
        <CheckBox
          onSelect={(val) => setFieldValue('interestRequirement.declare.a', val)}
          checked={values.interestRequirement.declare.a}
        />
        <p>
          I/we acknowledge that transfers of fund into this account must originate from my personal sole and or joint
          current account or savings account.
        </p>
      </div>

      <div className="checkbox-field">
        <CheckBox
          onSelect={(val) => setFieldValue('interestRequirement.declare.b', val)}
          checked={values.interestRequirement.declare.b}
        />
        <p>
          Any share(s) acquired by me/us under this account will not be held by me/us as a bare trustee for a body
          corporate, or for persons who include a body corporate.
        </p>
      </div>

      <div className="checkbox-field">
        <CheckBox
          onSelect={(val) => setFieldValue('interestRequirement.declare.c', val)}
          checked={values.interestRequirement.declare.c}
        />
        <p>
          I/We have read a copy of the last published Summary Financial Statement. Copies are available on request or
          can be downloaded from our website www.chorleybs.co.uk.
        </p>
      </div>

      <div className="checkbox-field">
        <CheckBox
          onSelect={(val) => setFieldValue('interestRequirement.declare.d', val)}
          checked={values.interestRequirement.declare.d}
        />
        <p>
          I/We have read a copy of and agree to be bound by the Society’s Memorandum of Rules. Copies are available on
          request or from our website www.chorleybs.co.uk.
        </p>
      </div>

      <div className="checkbox-field">
        <CheckBox
          onSelect={(val) => setFieldValue('interestRequirement.declare.e', val)}
          checked={values.interestRequirement.declare.e}
        />
        <p>
          I/We agree to be bound by the conditions relating to the Agreement to Assign Windfalls to Charity. Copies are
          available on request or from our website www.chorleybs.co.uk.
        </p>
      </div>

      <div className="checkbox-field">
        <CheckBox
          onSelect={(val) => setFieldValue('interestRequirement.declare.f', val)}
          checked={values.interestRequirement.declare.f}
        />
        <p>Other Income.</p>
      </div>

      <div className="spc30" />
      <p className="sub-text">
        View the Agreement to Assign Windfalls to Charity{' '}
        <a href={'/assignment-charity-windfalls.pdf'} target="_blank">
          here
        </a>
      </p>
      <div className="spc10" />
      <div className="spc30" />
      <div className="checkbox-field">
        <CheckBox
          onSelect={(val) => setFieldValue('interestRequirement.declare.g', val)}
          checked={values.interestRequirement.declare.g}
        />
        <p>
          I/We confirm this information is correct and I/we understand that I/we may request in writing , a copy of the
          details, which are held about me/us on the Society’s records.
        </p>
      </div>
      <div className="checkbox-field">
        <CheckBox
          onSelect={(val) => setFieldValue('interestRequirement.declare.h', val)}
          checked={values.interestRequirement.declare.h}
        />
        <p>
          I/We have read the Savings Account General Terms and Conditions and the marketing literature for the account
          I/we are applying for.
        </p>
      </div>
      <div className="spc30" />
      <p className="sub-text">
        View the Agreement to Assign Windfalls to Charity{' '}
        <a href={'/assignment-charity-windfalls.pdf'} target="_blank">
          here
        </a>
      </p>
      <div className="spc10" />
      <div className="spc30" />
      <div className="checkbox-field">
        <CheckBox
          onSelect={(val) => setFieldValue('interestRequirement.declare.i', val)}
          checked={values.interestRequirement.declare.i}
        />
        <p>
          I/We have read the Society’s Privacy Notice which describes the way my personal information will be processed.
        </p>
      </div>

      <div className="checkbox-field">
        <CheckBox
          onSelect={(val) => setFieldValue('interestRequirement.declare.j', val)}
          checked={values.interestRequirement.declare.j}
        />
        <p>
          I/We acknowledge that the information contained in this form and information regarding reportable account(s)
          may be provided to the tax authorities of the country in which this account(s) is/are maintained and exchanged
          with tax authorities of another country or countries in which I/we may be tax resident pursuant to
          intergovernmental agreements to exchange financial account information.
        </p>
      </div>

      <div className="checkbox-field">
        <CheckBox
          onSelect={(val) => setFieldValue('interestRequirement.declare.k', val)}
          checked={values.interestRequirement.declare.k}
        />
        <p>I/We acknowledge receipt of the FSCS information sheet.</p>
      </div>
      <div className="spc30" />
      <p className="sub-text">
        View the Agreement to Assign Windfalls to Charity{' '}
        <a href={'/assignment-charity-windfalls.pdf'} target="_blank">
          here
        </a>
      </p>
      <div className="spc30" />
      <div className="spc10" />
      <div className="checkbox-field">
        <CheckBox
          onSelect={(val) => setFieldValue('interestRequirement.declare.l', val)}
          checked={values.interestRequirement.declare.l}
        />
        <p>
          I/We undertake to advise Chorley Building Society within 30 days of any change in circumstances which affects
          my/our tax residency employmentStatus or causes information contained herein to become incorrect, and to
          provide Chorley Building Society with a suitably updated self-certification and interestRequirement within 30
          days of such change in circumstances.
        </p>
      </div>
      <div className="spc30" />
      <p>
        If you do not understand any point within the application, please ask for further information. You can call our
        Savers team on 01257 235003 or emailAddress us at{' '}
        <a href="mailto: savers@chorleybs.co.uk" className="link-text" target="_blank">
          <b>savers@chorleybs.co.uk</b>
        </a>
        .
      </p>

      {/* END */}
      <div className="spc30" />
      <div className="spc10" />
      <div className="buttons">
        <button className="btn btn--secondary" onClick={onPrev}>
          Previous
        </button>
        <button className="btn" onClick={onNext} disabled={!!errors.interestRequirement}>
          Next
        </button>
      </div>
    </Div>
  );
};
